import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Add this import
import { 
  FiMenu,
  FiBell,
  FiLogOut,
  FiMap,
  FiNavigation,
  FiUsers,
  FiArrowLeft,
  FiList,
  FiPlus,
  FiEye,
  FiFilter,
  FiSettings,
  FiCheckSquare,
  FiMapPin,
  FiHome,
  FiMessageSquare,
  FiHelpCircle,
  FiSearch,
  FiGlobe, // Add globe icon
} from 'react-icons/fi';
import logo from '../../../components/assets/logos/logo2.PNG';
import { logout } from '../../redux/Slices/authSlice';

// Management Card Component
const ManagementCard = ({ title, count, icon: Icon, color, description, onClick }) => {
  const { t } = useTranslation(); // Add translation hook
  return (
    <div className="management-card" onClick={onClick}>
      <div className="management-card__header">
        <div className="management-card__icon-wrapper">
          <div className="management-card__icon" style={{ background: color }}>
            <Icon size="1.8em" />
          </div>
          <span className="management-card__count">{count}</span>
        </div>
        <h3 className="management-card__title">{t(title) || title}</h3>
      </div>
      <div className="management-card__content">
        <p className="management-card__description">{t(description) || description}</p>
        <div className="management-card__actions">
          <button className="btn-management btn-view">
            {t('manageNow', 'Manage Now')} →
          </button>
        </div>
      </div>
    </div>
  );
};

// Action Card Component
const ActionCard = ({ title, description, icon: Icon, color, onClick }) => {
  const { t } = useTranslation(); // Add translation hook
  return (
    <div className="action-card" onClick={onClick}>
      <div className="action-card__icon-wrapper" style={{ background: color }}>
        <Icon size="2em" />
      </div>
      <h3 className="action-card__title">{t(title) || title}</h3>
      <p className="action-card__description">{t(description) || description}</p>
      <div className="action-card__arrow">→</div>
    </div>
  );
};

// Management Page Layout Component
const ManagementPage = ({ title, description, onBack, children }) => {
  const { t } = useTranslation(); // Add translation hook
  return (
    <div className="management-page">
      <div className="management-page__header">
        <button className="btn-back" onClick={onBack}>
          <FiArrowLeft /> {t('backToDashboard', 'Back to Dashboard')}
        </button>
        <div className="management-page__title-section">
          <h1>{t(title) || title}</h1>
          <p>{t(description) || description}</p>
        </div>
      </div>
      <div className="management-page__content">
        {children}
      </div>
    </div>
  );
};

// Bus Stops Management Component
const BusStopsManagement = ({ onBack, userRole }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(); // Add translation hook
  
  const baseActionCards = [
    {
      id: 1,
      title: 'registerNewBusStop',
      titleText: 'Register New Bus Stop',
      description: 'addNewBusStopDescription',
      descriptionText: 'Add a new bus stop with location and facility details',
      icon: FiPlus,
      color: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
      onClick: () => navigate('/bus-finder/bus-stops/register')
    },
    {
      id: 2,
      title: 'manageBusStops',
      titleText: 'Manage Bus Stops',
      description: 'manageBusStopsDescription',
      descriptionText: 'View, update, or delete bus stop information',
      icon: FiEye,
      color: 'linear-gradient(135deg, #3F51B5 0%, #303F9F 100%)',
      onClick: () => navigate('/bus-finder/bus-stops/manage')
    }
  ];

  // Add approval card for superadmin
  const approvalCard = {
    id: 3,
    title: 'busStopApprovals',
    titleText: 'Bus Stop Approvals',
    description: 'approveRejectBusStopDescription',
    descriptionText: 'Approve or reject pending bus stop registrations',
    icon: FiCheckSquare,
    color: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)',
    onClick: () => navigate('/bus-finder/bus-stops/approvals')
  };

  const actionCards = userRole === 'superadmin' 
    ? [...baseActionCards, approvalCard]
    : baseActionCards;

  return (
    <ManagementPage
      title="busStopsManagement"
      titleText="Bus Stops Management"
      description="manageBusStopsDescriptionMain"
      descriptionText="Manage bus stops, locations, and approvals"
      onBack={onBack}
    >
      <div className="action-cards-grid">
        {actionCards.map((card) => (
          <ActionCard 
            key={card.id} 
            title={card.title}
            description={card.description}
            icon={card.icon}
            color={card.color}
            onClick={card.onClick}
          />
        ))}
      </div>
    </ManagementPage>
  );
};

// Routes Management Component
const RoutesManagement = ({ onBack }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation(); // Add translation hook
  const isSuperAdmin = user.role === 'superadmin';
  
  // Base action cards for all users
  const baseActionCards = [
    {
      id: 1,
      title: 'addNewRoute',
      titleText: 'Add New Route',
      description: 'createNewRouteDescription',
      descriptionText: 'Create new bus routes with stops, schedules, and pricing',
      icon: FiPlus,
      color: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      onClick: () => navigate('/route-finder/routes/add')
    },
    {
      id: 2,
      title: 'manageRoutes',
      titleText: 'Manage Routes',
      description: 'manageRoutesDescription',
      descriptionText: 'View, edit, or delete existing bus routes',
      icon: FiList,
      color: 'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)',
      onClick: () => navigate('/route-finder/routes/manage')
    },
  ];

  // Additional action cards for superadmin only
  const superAdminActionCards = [
    {
      id: 3,
      title: 'manageRouteApprovals',
      titleText: 'Manage Route Approvals',
      description: 'reviewRouteApprovalsDescription',
      descriptionText: 'Review and approve pending route submissions',
      icon: FiCheckSquare,
      color: 'linear-gradient(135deg, #FF9800 0%, #FF5722 100%)',
      onClick: () => navigate('/route-finder/routes/approvals')
    }
  ];

  // Combine cards based on user role
  const actionCards = isSuperAdmin 
    ? [...baseActionCards, ...superAdminActionCards]
    : baseActionCards;

  return (
    <ManagementPage
      title="routesManagement"
      titleText="Routes Management"
      description="manageAllRoutesDescription"
      descriptionText="Manage all bus routes and schedules"
      onBack={onBack}
    >
      {isSuperAdmin && (
        <div className="admin-badge">
          <span className="admin-badge__text">{t('superAdminView', 'Super Admin View')}</span>
          <span className="admin-badge__dot"></span>
        </div>
      )}
      <div className="action-cards-grid">
        {actionCards.map((card) => (
          <ActionCard 
            key={card.id} 
            title={card.title}
            description={card.description}
            icon={card.icon}
            color={card.color}
            onClick={card.onClick}
          />
        ))}
      </div>
    </ManagementPage>
  );
};

const GroupRoutesManagement = ({ onBack }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation(); // Add translation hook
  const isSuperAdmin = user.role === 'superadmin';
  
  // Base action cards for all users
  const baseActionCards = [
    {
      id: 1,
      title: 'addNewVillageCity',
      titleText: 'Add new Village/City',
      description: 'registerNewVillageCityDescription',
      descriptionText: 'Register a new Village/City',
      icon: FiUsers,
      color: 'linear-gradient(135deg, #FF9800 0%, #FF5722 100%)',
      onClick: () => navigate('/route-finder/group-routes/create')
    },
    {
      id: 2,
      title: 'manageVillageCity',
      titleText: 'Manage Village/City',
      description: 'manageVillageCityDescription',
      descriptionText: 'View/Alter/Delete the details of a Village/City',
      icon: FiEye,
      color: 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
      onClick: () => navigate('/route-finder/group-routes/manage')
    },
  ];

  // Additional action cards for superadmin only
  const superAdminActionCards = [
    {
      id: 3,
      title: 'manageRegionApprovals',
      titleText: 'Manage Region Approvals',
      description: 'reviewRegionApprovalsDescription',
      descriptionText: 'Review and approve pending village/city submissions',
      icon: FiCheckSquare,
      color: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)',
      onClick: () => navigate('/route-finder/group-routes/approvals')
    }
  ];

  // Combine cards based on user role
  const actionCards = isSuperAdmin 
    ? [...baseActionCards, ...superAdminActionCards]
    : baseActionCards;

  return (
    <ManagementPage
      title="villageCityManagement"
      titleText="Village/City Management"
      description="manageGeoDetailsDescription"
      descriptionText="Manage geo-spatial details of various spots and regions"
      onBack={onBack}
    >
      {isSuperAdmin && (
        <div className="admin-badge">
          <span className="admin-badge__text">{t('superAdminView', 'Super Admin View')}</span>
          <span className="admin-badge__dot"></span>
        </div>
      )}
      <div className="action-cards-grid">
        {actionCards.map((card) => (
          <ActionCard 
            key={card.id} 
            title={card.title}
            description={card.description}
            icon={card.icon}
            color={card.color}
            onClick={card.onClick}
          />
        ))}
      </div>
    </ManagementPage>
  );
};

// Sidebar Component
const Sidebar = ({ isCollapsed, toggleCollapse }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const { user } = useSelector((state) => state.auth);
  const { t } = useTranslation(); // Add translation hook
  
  // Same menu items as your main dashboard
  const menuItems = [
    { id: 1, icon: FiHome, label: t('dashboard', 'Dashboard'), path: '/dashboard' },
    { id: 2, icon: FiSearch, label: t('busFinder', 'Bus Finder'), path: '/busfinderpanel' },
    { id: 3, icon: FiNavigation, label: t('routeFinder', 'Route Finder'), path: '/route-finder-panel' },
    // { id: 4, icon: FiMessageSquare, label: t('notices', 'Notices'), path: '/notices' },
    // { id: 5, icon: FiHelpCircle, label: t('guide', 'Guide'), path: '/guide' },
    // { id: 6, icon: FiSettings, label: t('settings', 'Settings'), path: '/settings' },
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleMenuItemClick = (item) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar__header">
        <button className="sidebar__toggle" onClick={toggleCollapse}>
          <FiMenu size={24} />
        </button>
        {!isCollapsed && <h3 className="sidebar__logo">{t('mainMenu', 'Main Menu')}</h3>}
      </div>

      <nav className="sidebar__menu">
        {menuItems.map((item) => (
          <div
            key={item.id}
            className={`sidebar__item ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => handleMenuItemClick(item)}
            style={{ cursor: 'pointer' }}
          >
            <item.icon size={20} className="sidebar__icon" />
            {!isCollapsed && <span className="sidebar__label">{item.label}</span>}
          </div>
        ))}
      </nav>

      <div className="sidebar__footer">
        <div className="sidebar__item logout" onClick={handleLogout}>
          <FiLogOut size={20} className="sidebar__icon" />
          {!isCollapsed && <span className="sidebar__label">{t('logout', 'Logout')}</span>}
        </div>
      </div>
    </div>
  );
};

// Main RouteFinderPanel Component
const RouteFinderPanel = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'routes', 'groupRoutes', 'busStops'
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { t, i18n } = useTranslation(); // Add translation hook
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);

  // Management Cards Data
  const managementCards = [
    {
      id: 1,
      title: 'manageBusRoutes',
      titleText: 'Manage Bus Routes',
      icon: FiMap,
      color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      description: 'manageAllBusRoutesDescription',
      descriptionText: 'Manage all bus routes, schedules, stops, and route details',
      onClick: () => setCurrentView('routes')
    },
    {
      id: 2,
      title: 'geoSpotsRegions',
      titleText: 'Geo-spatial spots&regions',
      icon: FiUsers,
      color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      description: 'manageGeoSpotsDescription',
      descriptionText: 'Manage the geo-spatial details of various spots&regions',
      onClick: () => setCurrentView('groupRoutes')
    },
    {
      id: 3,
      title: 'manageBusStopsCard',
      titleText: 'Manage Bus Stops',
      icon: FiMapPin,
      color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      description: 'manageBusStopsLocationsDescription',
      descriptionText: 'Manage bus stops, locations, and facilities',
      onClick: () => setCurrentView('busStops')
    }
  ];

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageDropdown(false);
  };

  const Navbar = () => {
    const userName = user.name || user.userName || user.role || t('guestUser', 'Guest User');
    const userRole = user.role ? 
      user.role.charAt(0).toUpperCase() + user.role.slice(1) : 
      t('unknownRole', 'Unknown Role');
    const userStatus = user.email || t('offline', 'Offline'); 
    const isOnline = !!user.email;
    const isSuperAdmin = user.role === 'superadmin';

    return (
      <header className="navbar">
        <div className="navbar__left">
          <img 
            src={logo} 
            alt={t('logoAlt', 'Rajdoot Logo')}
            style={{
              width: 150,
              height: 70,
              display: 'block',
            }}
          />
        </div>

        <div className="navbar__right">
          <div className="language-switcher-container" style={{ position: 'relative' }}>
            {/* <button 
              className="navbar__icon-frame language-button"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '8px',
                borderRadius: '4px',
                transition: 'background-color 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <FiGlobe size={24} className="navbar__icon" />
              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                {i18n.language === 'en' ? 'EN' : 'HI'}
              </span>
            </button> */}
            
            {/* {showLanguageDropdown && (
              <div 
                className="language-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  padding: '8px',
                  minWidth: '100px',
                  zIndex: 1000
                }}
              >
                <button 
                  onClick={() => changeLanguage('en')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    textAlign: 'left',
                    background: i18n.language === 'en' ? '#f0f0f0' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '3px',
                    marginBottom: '4px'
                  }}
                >
                  English
                </button>
                <button 
                  onClick={() => changeLanguage('hi')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    textAlign: 'left',
                    background: i18n.language === 'hi' ? '#f0f0f0' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '3px'
                  }}
                >
                  हिन्दी
                </button>
              </div>
            )} */}
          </div>

        
          <div className="navbar__user-profile">
            <div className="navbar__user-avatar">
              <span style={{alignContent:'center'}}>
                {userName.split(' ').map(n => n[0]).join('')}
              </span>
            </div>

            <div className="navbar__user-info">
              <div className="navbar__user-name-role">
                <p className="navbar__user-role">{userRole}</p> 
              </div>
              
              <div className="navbar__user-status">
                {isOnline && <span className="navbar__status-dot"></span>}
                <span className="navbar__status-text">{userStatus}</span>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  };

  const DashboardView = () => {
    const { t } = useTranslation(); // Add translation hook for DashboardView
    const isSuperAdmin = user.role === 'superadmin';
    
    return (
      <>
        {isSuperAdmin && (
          <div className="admin-notice">
           
          </div>
        )}
        
        <div className="management-section">
          <div className="management-grid">
            {managementCards.map((card) => (
              <ManagementCard 
                key={card.id} 
                title={card.title}
                count={card.count}
                icon={card.icon}
                color={card.color}
                description={card.description}
                onClick={card.onClick}
              />
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="dashboard-app-container"> 
      <Navbar />
      <div className={`dashboard-content-wrapper ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Using the same Sidebar component as your main dashboard */}
        <Sidebar isCollapsed={isCollapsed} toggleCollapse={toggleCollapse} />
        
        <div className="dashboard__main">
          <div className="dashboard__content">
            {currentView === 'dashboard' && <DashboardView />}
            {currentView === 'routes' && <RoutesManagement onBack={() => setCurrentView('dashboard')} />}
            {currentView === 'groupRoutes' && <GroupRoutesManagement onBack={() => setCurrentView('dashboard')} />}
            {currentView === 'busStops' && (
              <BusStopsManagement 
                onBack={() => setCurrentView('dashboard')} 
                userRole={user.role}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteFinderPanel;